/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, Type } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationConfig } from '../notification-config';
import { NOTIFICATION_DATA } from '../notification-data-token';
import { NotificationType } from '../notification-type';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
    selector: 'ngf-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeInUp', [
            transition(':enter', [
                style({ transform: 'translate3d(0, 100%, 0)', opacity: 0 }),
                animate('300ms cubic-bezier(0.500, 0.755, 0.020, 1.000)', style({ transform: 'translate3d(0, 0, 0)', opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('1.6s', style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class NotificationComponent implements OnDestroy {
  private static readonly DEFAULT_LIFETIME = 7000;
  private static readonly INFINITE_LIFETIME = -1;

  constructor(private injector: Injector,
              private liveAnnouncer: LiveAnnouncer,
              private changeDetectorRef: ChangeDetectorRef) {}

  notifications: NotificationConfig[] = [];

  readonly NotificationType = NotificationType;

  private readonly ngUnsubscribe$ = new Subject<void>();

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  push(notification: NotificationConfig): void {
    this.notifications.push(notification);
    this.changeDetectorRef.markForCheck(); 

    // TODO: FLO-50723 - Should this just be default behavior
    // rather than opt-in?
    if (notification.announce && 'text' in notification) {
      this.liveAnnouncer.announce(
        `${notification.text}`,
        'assertive'
      );
    }

    if (notification.lifetime !== NotificationComponent.INFINITE_LIFETIME) {
      this.dismissAfterLifetime(notification);
    }
  }

  dismiss(index: number): void {
    this.notifications.splice(index, 1);
  }

  getComponentInjector(notification: { type: NotificationType, component: Type<any>, data?: any }): Injector {
    return Injector.create({
        providers: [
          {provide: NOTIFICATION_DATA, useValue: notification.data}
        ],
        parent: this.injector
      },
    );
  }

  private dismissAfterLifetime(notification: NotificationConfig): void {
    timer(notification.lifetime ?? NotificationComponent.DEFAULT_LIFETIME)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => {
        if (this.notifications.indexOf(notification) !== -1) {
          this.dismiss(this.notifications.indexOf(notification));
          this.changeDetectorRef.markForCheck();
        }
      });
  }
}
